import React, { useState, useContext, useCallback, useEffect } from 'react';

import PresentPC from 'components/pages/AddInfo/PresentPC';
import PresentSP from 'components/pages/AddInfo/PresentSP';
import TextInput from 'components/atoms/Input/TextInput';
import PcErrorArea from 'components/molecules/PcErrorArea';
import BasicCheckBoxInput from 'components/atoms/Input/BasicCheckBoxInput';

import {
  UPDATE_ADD_INFO,
  CHANGE_VALID
} from 'fluxes/actions';
import AppContext from 'fluxes/context';

import {
  useWindowDimensions,
  useIECheck,
  sortErrors,
  useDirectTransCheck,
  isSchemaValid,
  scrollTop,
} from 'utils';
import MeasureEvent from 'utils/measureEvent';
import {
  MEASURE_MAP,
  CHOICES_MAP,
} from 'utils/constant';

const AddInfo = ()=>{
  const path = window.location.pathname;
  const { state, dispatch } = useContext(AppContext);
  const [ showLoading, setShowLoading ] = useState(false);

  const[ companyNameText, setCompanyNameText ] = useState(state.companyNameText);
  const[ dept, setDept ] = useState(state.dept);
  const[ chargePersonNameText, setChargePersonNameText ] = useState(state.chargePersonNameText);
  const[ tel, setTel ] = useState(state.tel);
  const[ email, setEmail ] = useState(state.email);
  const[ address, setAddress ] = useState(state.address);
  const[ employmentPlacementDispatching, setEmploymentPlacementDispatching ] = useState(state.employmentPlacementDispatching);
        
  
  // レスポンシブ
  const { width } = useWindowDimensions();
  const isIE = useIECheck();
  const isSp = width <= 640 && !isIE;

  // 直接遷移チェック
  useDirectTransCheck({ state });

  // 入力チェック用変数
  const initialValid = { valid: state.validMap[path], errorMessages: [] };
  const [ valids, setValids ] = useState({
    schema: initialValid,
  });
  
  // 初期入力チェック設定
  const [ isShowError, setIsShowError ] = useState({
    companyNameText: false,
  });
  
  // エラーの画面表示で利用する値
  const valid = Object.keys(valids).every(key => valids[key].valid);

  const dispErrors = useCallback(() => {
    const { schema } = valids;
    const errors = sortErrors([
      ...schema.errorMessages,
    ]);
    const errorMap = errors.reduce((obj, error) => {
      const { target, message } = error;
      if(!isShowError[target] || Object.keys(obj).includes(target)) return obj;
      return { ...obj, [target]: message };
    }, {});
    return errorMap;
  }, [ isShowError, valids ]);

  // 入力チェック（全体）
  useEffect(() => {
    const [isValid, newErrorMessages] = isSchemaValid({
      target: {
        companyNameText,
        dept,
        chargePersonNameText,
        tel,
        email,
        address,
        employmentPlacementDispatching,
      },
    });

    // erros更新
    const newValid = { schema: { valid: isValid, errorMessages: newErrorMessages } };
    setValids(org => ({ ...org, ...newValid }));
  }, [
    companyNameText,
    dept,
    chargePersonNameText,
    tel,
    email,
    address,
    employmentPlacementDispatching,
    valid,
  ]);


  // onChange
  const onChangeCompanyNameText = async (e) =>{
    setCompanyNameText(e.target.value);
    if(!isShowError.companyNameText) {
      setIsShowError({ ...isShowError, companyNameText: true });
    }
  };

  const onChangeDept = (e) =>{
    setDept(e.target.value);

    if(!isShowError.dept) {
      setIsShowError({ ...isShowError, dept: true });
    }
  };


  const onChangeChargePersonNameText = (e) =>{
    setChargePersonNameText(e.target.value);
    if(!isShowError.chargePersonNameText) {
      setIsShowError({ ...isShowError, chargePersonNameText: true });
    }
  };

  const onChangeTel = (e) =>{
    setTel(e.target.value);

    if(!isShowError.tel) {
      setIsShowError({ ...isShowError, tel: true });
    }
  };

  const onChangeEmail = (e) =>{
    setEmail(e.target.value);

    if(!isShowError.email) {
      setIsShowError({ ...isShowError, email: true });
    }
  };

  const onChangeAddress = (e) =>{
    setAddress(e.target.value);
    if(!isShowError.address) {
      setIsShowError({ ...isShowError, address: true });
    }
  };

  const onChangeEmploymentPlacementDispatching = () =>{
    if (employmentPlacementDispatching) {
      setEmploymentPlacementDispatching(false);
    } else {
      setEmploymentPlacementDispatching(true);
    } 
  };

  // ボタン押下
  // state更新処理
  const updateAddInfoState = ()=>{
    dispatch({
      type: UPDATE_ADD_INFO,
      updAddInfo: {
        companyNameText,
        dept,
        chargePersonNameText,
        tel,
        email,
        address,
        employmentPlacementDispatching,
      },
    });
  };
  const onClickMainButton = {
    prev: () => {
      scrollTop();
      updateAddInfoState();
      dispatch({
        type: CHANGE_VALID,
        path,
        valid,
      });
    },
    next: () => {
      // イベント計測
      MeasureEvent(
        {
          linkTrackVars: MEASURE_MAP.EVENT_82.linkTrackVars,
          linkTrackEvents: MEASURE_MAP.EVENT_82.linkTrackEvents,
          events: MEASURE_MAP.EVENT_82.events,
        },
        MEASURE_MAP.EVENT_82.name,
      );
      scrollTop();
      updateAddInfoState();
      dispatch({
        type: CHANGE_VALID,
        path,
        valid,
      });
    },
  };


  // 表示用画面項目作成
  const basicInfoList = [
    {
      title: '企業名',
      label: '必須',
      questionMark: false,
      type: 'TextInput',
      input: <>
        <TextInput
          name='companyNameText'
          val={companyNameText}
          onChange={onChangeCompanyNameText}
          placeholder='株式会社リクルートスタッフィング'
          maxLength='300'
          inValid={!!dispErrors().companyNameText}
          suggestText={false}
        />
        <PcErrorArea position='custom' message={dispErrors().companyNameText} />
      </>,
    },
    {
      title: '派遣先部課名',
      label: '必須',
      questionMark: true,
      popUpTitle: '',
      popUpDetail: (
        <span>
          派遣先部課名がない場合は「なし」と入力してください
        </span>),
      type: 'TextInput',
      input: <>
        <TextInput
          name='dept'
          val={dept}
          onChange={onChangeDept}
          placeholder='人事部　人事課'
          maxLength='150'
          inValid={!!dispErrors().dept}
        />
        <PcErrorArea position='custom' message={dispErrors().dept} />
      </>
    },
    {
      title: 'ご担当者名',
      label: '必須',
      questionMark: false,
      type: 'TextInput',
      input: <>
        <TextInput
          name='chargePersonNameText'
          val={chargePersonNameText}
          onChange={onChangeChargePersonNameText}
          placeholder='派遣　太郎'
          maxLength='150'
          inValid={!!dispErrors().chargePersonNameText}
        />
        <PcErrorArea position='custom' message={dispErrors().chargePersonNameText} />
      </>,
    },
    {
      title: '電話番号',
      label: '必須',
      questionMark: false,
      type: 'TextInput',
      input: <>
        <TextInput
          name='tel'
          val={tel}
          onChange={onChangeTel}
          placeholder='090123456789'
          maxLength='13'
          inValid={!!dispErrors().tel}
        />
        <PcErrorArea message={dispErrors().tel} />
      </>,
    },
    {
      title: 'メールアドレス',
      label: '必須',
      questionMark: false,
      type: 'TextInput',
      input: <>
        <TextInput
          name='email'
          val={email}
          onChange={onChangeEmail}
          placeholder='haken@r-staffing.co.jp'
          inValid={!!dispErrors().email}
        />
        <PcErrorArea message={dispErrors().email} />
      </>,
    },
    {
      title: '派遣先住所',
      subTitle: '派遣スタッフが実際に就業する住所を、番地・建物名まで記載してください。',
      label: '必須',
      questionMark: false,
      type: 'TextInput',
      input: <>
        <TextInput
          name='address'
          val={address}
          onChange={onChangeAddress}
          placeholder='東京都千代田区有楽町1‐13‐1 〇〇ビル19F'
          maxLength='300'
          inValid={!!dispErrors().address}
        />
        <div className='address'>
          <PcErrorArea position='relative' message={dispErrors().address} />
        </div>
      </>,
    },
    {
      title: '紹介予定派遣のみの希望',
      label: '任意',
      questionMark: null,
      popUpTitle: '紹介予定派遣について',
      popUpDetail: (
        <span>
          <p>
            「紹介予定派遣」とは、社員として採用することを前提に、派遣社員を一定期間受入、派遣期間中に社員としての適性を見極めることができる仕組みです。
            <br />
            <br />
            詳細は
            <a className="link-grey" href="/sol/contents/client/ttoh/" target="_blank" rel="noopener noreferrer">こちら</a>
          </p>
        </span>
      ),
      type: 'TextInput',
      input: <>
        <BasicCheckBoxInput
          id='employmentPlacementDispatching'
          value={employmentPlacementDispatching}
          label={CHOICES_MAP.employmentPlacementDispatchingLabel}
          onChange={onChangeEmploymentPlacementDispatching}
          checked={employmentPlacementDispatching}
        />
      </>,
    },
  ];


  return isSp ? (
    <PresentSP
      // state
      basicInfoList={basicInfoList}

      // onClick
      onClickMainButton={onClickMainButton}

      // valid
      valid={valid}
      showLoading={showLoading}
      errorDispMap={dispErrors()}
    />
  ) : (
    <PresentPC
      // state
      basicInfoList={basicInfoList}

      // onClick
      onClickMainButton={onClickMainButton}

      // valid
      valid={valid}
      showLoading={showLoading}
      errorDispMap={dispErrors()}
    />
  );
};

export default AddInfo;